import { lazy, Suspense, useEffect, useState, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import ScrollController from "./components/ScrollToTop";
import SplashScreen from "./components/SplashScreen";
import { AuthProvider } from "./contexts/auth/auth-provider";
import { AuthGuard } from "./contexts/auth/auth-guard";
import { GuestGuard } from "./contexts/auth/guest-guard";
import { CreatorOnlyRoute } from "./components/route-guards/CreatorOnlyRoute";
import { UserRoleProvider } from "./contexts/user-role-context";
import { CookieConsent } from "./components/CookieConsent";
import { useAnalytics } from "./hooks/useAnalytics";
import { useAuthContext } from "./hooks/use-auth-context";

export type UserRole = "creator" | "student";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/dashboard/Products"));
const CreateCourse = lazy(() => import("./pages/dashboard/CreateCourse"));
const CreateProduct = lazy(() => import("./pages/dashboard/CreateProduct"));
const CreateDownload = lazy(() => import("./pages/dashboard/CreateDownload"));
const CreateBooking = lazy(() => import("./pages/dashboard/CreateBooking"));
const CoursePlanner = lazy(() => import("./pages/dashboard/CoursePlanner"));
const Discounts = lazy(() => import("./pages/dashboard/Discounts"));
const Sales = lazy(() => import("./pages/dashboard/Sales"));
const Bookings = lazy(() => import("./pages/dashboard/Bookings"));
const Students = lazy(() => import("./pages/dashboard/Students"));
const Communities = lazy(() => import("./pages/dashboard/Communities"));
const CommunityDetail = lazy(() => import("./pages/dashboard/CommunityDetail"));
const Messages = lazy(() => import("./pages/dashboard/Messages"));
const Reviews = lazy(() => import("./pages/dashboard/Reviews"));
const Payouts = lazy(() => import("./pages/dashboard/Payouts"));
const Transactions = lazy(() => import("./pages/dashboard/Transactions"));
const Referrals = lazy(() => import("./pages/dashboard/Referrals"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const GuardedCourseView = lazy(() => import("./pages/learn/GuardedCourseView"));
const CommunityView = lazy(() => import("./pages/learn/CommunityView"));
const GuardedDownloadView = lazy(() => import("./pages/products/GuardedDownloadView"));
const GuardedBookingView = lazy(() => import("./pages/products/GuardedBookingView"));
const ProductLandingPage = lazy(() => import("./pages/public/ProductLandingPage"));
const CreatorProfile = lazy(() => import("./pages/public/CreatorProfile"));
const ReferralLanding = lazy(() => import("./pages/public/ReferralLanding"));
const FeeCalculator = lazy(() => import("./pages/public/FeeCalculator"));
const Marketplace = lazy(() => import("./pages/public/Marketplace"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const Cookies = lazy(() => import("./pages/public/Cookies"));
const Help = lazy(() => import("./pages/public/Help"));
const Pricing = lazy(() => import("./pages/public/Pricing"));
const CompareGumroad = lazy(() => import("./pages/public/CompareGumroad"));
const CompareTeachable = lazy(() => import("./pages/public/CompareTeachable"));
const ComparePodia = lazy(() => import("./pages/public/ComparePodia"));
const Blog = lazy(() => import("./pages/public/Blog"));
const BlogPost = lazy(() => import("./pages/public/BlogPost"));
const CourseAnalytics = lazy(() => import("./pages/analytics/CourseAnalytics"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const Login = lazy(() => import("./pages/auth/Login"));
const Verify = lazy(() => import("./pages/auth/Verify"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const PostAuth = lazy(() => import("./pages/auth/PostAuth"));
const Mfa = lazy(() => import("./pages/auth/Mfa"));
const ProductAccess = lazy(() => import("./pages/auth/ProductAccess"));
const AdminPortal = lazy(() => import("./pages/admin/AdminPortal"));
const NotFound = lazy(() => import("./pages/NotFound"));

// preload Dashboard when on auth pages
const PreloadHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/auth/")) return;

    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void) => number);
    const cancelRic = (window as any).cancelIdleCallback as undefined | ((id: number) => void);

    if (typeof ric === 'function') {
      idleId = ric(() => { void import("./pages/Dashboard"); });
    } else {
      timeoutId = setTimeout(() => { void import("./pages/Dashboard"); }, 0);
    }

    return () => {
      if (idleId && typeof cancelRic === 'function') cancelRic(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  return null;
};

// keep a single QueryClient across HMR
const queryClient = (globalThis as any).__qc ?? new QueryClient();
(globalThis as any).__qc = queryClient;

// track page views
const PageViewTracker = () => {
  const location = useLocation();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location.pathname, analytics]);

  return null;
};

// identify authenticated users
const UserIdentifier = () => {
  const { user } = useAuthContext();
  const analytics = useAnalytics();

  useEffect(() => {
    if (user?.id && analytics.isEnabled) {
      // only identify with user ID, don't send PII
      analytics.identify(user.id, {
        role: user.role,
      });
    }
  }, [user?.id, user?.role, analytics]);

  return null;
};

const AppContent = () => {
  const [userRole, setUserRole] = useState<UserRole>(() => {
    const savedRole = localStorage.getItem("lint.userRole");
    return (savedRole === "student" ? "student" : "creator") as UserRole;
  });

  const navigate = useNavigate();
  const location = useLocation();
  const previousRoleRef = useRef<UserRole>(userRole);

  useEffect(() => {
    localStorage.setItem("lint.userRole", userRole);
  }, [userRole]);

  useEffect(() => {
    if (previousRoleRef.current !== userRole) {
      if (userRole === "student") {
        navigate("/dashboard/products", { replace: true });
      } else if (userRole === "creator" && location.pathname === "/dashboard/products") {
        navigate("/dashboard", { replace: true });
      }
      previousRoleRef.current = userRole;
    }
  }, [userRole, navigate, location.pathname]);

  return (
    <UserRoleProvider value={{ userRole, setUserRole }}>
      <ScrollController />
      <PreloadHandler />
      <PageViewTracker />
      <UserIdentifier />
      <Suspense fallback={<SplashScreen />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/ref/:code" element={<ReferralLanding />} />
          <Route path="/fee-calculator" element={<FeeCalculator />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/help" element={<Help />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/compare/gumroad" element={<CompareGumroad />} />
          <Route path="/compare/teachable" element={<CompareTeachable />} />
          <Route path="/compare/podia" element={<ComparePodia />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          <Route
            path="/auth/signup"
            element={
              <GuestGuard>
                <SignUp />
              </GuestGuard>
            }
          />
          <Route
            path="/auth/login"
            element={
              <GuestGuard>
                <Login />
              </GuestGuard>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <GuestGuard>
                <ForgotPassword />
              </GuestGuard>
            }
          />
          
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/post-auth" element={<PostAuth />} />
          <Route path="/auth/mfa" element={<Mfa />} />
          <Route path="/auth/access" element={<ProductAccess />} />
          
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Dashboard />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/products"
            element={
              <AuthGuard>
                <Products />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/products/new"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CreateProduct />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/products/new/course"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CreateCourse />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/products/new/download"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CreateDownload />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/products/new/booking"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CreateBooking />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/create-course"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CreateCourse />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/plan-course"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CoursePlanner />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/discounts"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Discounts />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/sales"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Sales />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/bookings"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Bookings />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/students"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Students />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/communities"
            element={
              <AuthGuard>
                <Communities />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/communities/:communityId"
            element={
              <AuthGuard>
                <CommunityDetail />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/messages"
            element={
              <AuthGuard>
                <Messages />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/reviews"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Reviews />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/payouts"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Payouts />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/transactions"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Transactions />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/referrals"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <Referrals />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />

          {/* Admin (hidden-ish, backend-gated) */}
          <Route
            path="/321_admin_sr_bless_opoku"
            element={
              <AuthGuard>
                <AdminPortal />
              </AuthGuard>
            }
          />
          
          {/* Analytics */}
          <Route
            path="/analytics/:productId"
            element={
              <AuthGuard>
                <CreatorOnlyRoute>
                  <CourseAnalytics />
                </CreatorOnlyRoute>
              </AuthGuard>
            }
          />
          
          {/* Public product views */}
          <Route path="/p/:productId" element={<ProductLandingPage />} />
          <Route path="/creator/:id" element={<CreatorProfile />} />
          <Route path="/learn/:courseId" element={<GuardedCourseView />} />
          <Route 
            path="/community/:communityId" 
            element={
              <AuthGuard>
                <CommunityView />
              </AuthGuard>
            } 
          />
          <Route path="/products/:productId/download" element={<GuardedDownloadView />} />
          <Route path="/products/:productId/booking" element={<GuardedBookingView />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </UserRoleProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </BrowserRouter>
          <CookieConsent />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
